import { capitalize } from 'lodash';

import type { DoohReportScreen } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';

export type SortDirection = 'asc' | 'desc';
export type DeliveryDriversSortableColumn = 'ota' | 'playouts' | 'impressions' | 'onTarget';
export type DeliveryDriversSortState = { columnId: DeliveryDriversSortableColumn; direction: SortDirection };

export const DEFAULT_SORT_STATE: DeliveryDriversSortState = { columnId: 'ota', direction: 'desc' };

export function isDeliveryDriversSortableColumn(columnId: string): columnId is DeliveryDriversSortableColumn {
  return columnId === 'ota' || columnId === 'playouts' || columnId === 'impressions' || columnId === 'onTarget';
}

export const SCREEN_SORT_ACCESSORS: Record<DeliveryDriversSortableColumn, (screen: DoohReportScreen) => number | null> =
  {
    impressions: screen => screen.impressions,
    onTarget: screen => screen.onTargetPct,
    ota: screen => screen.ota,
    playouts: screen => screen.playouts
  };

/** Sorts by a numeric accessor. Nulls always sink to the end regardless of direction. */
export function sortByAccessor<T>(items: T[], accessor: (item: T) => number | null, direction: SortDirection): T[] {
  return [...items].sort((a, b) => {
    const aValue = accessor(a);
    const bValue = accessor(b);
    if (aValue == null) {
      return bValue == null ? 0 : 1;
    }
    if (bValue == null) {
      return -1;
    }
    return direction === 'asc' ? aValue - bValue : bValue - aValue;
  });
}

export function paginate<T>(items: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function getTotalPages(itemCount: number, pageSize: number): number {
  return itemCount === 0 ? 1 : Math.ceil(itemCount / pageSize);
}

export function clampPage(page: number, totalPages: number): number {
  return Math.min(Math.max(page, 1), totalPages);
}

/** `allowedAdTypes` entries, capitalized for display, e.g. `["display"]` -> `["Display"]`. */
export function formatAdTypeLabels(allowedAdTypes: string[]): string[] {
  return allowedAdTypes.map(adType => capitalize(adType));
}

export function hasAudioAdType(allowedAdTypes: string[]): boolean {
  return allowedAdTypes.some(adType => adType.toLowerCase() === 'audio');
}
